'use client'

import { type ReactElement, useEffect, useState } from 'react'

type ExampleComponent = () => ReactElement

type ExampleModule = Record<string, unknown>

type Props = {
  name: string
  folder: string
}

export function ExampleLoader({ name, folder }: Props) {
  const [content, setComponent] = useState<undefined | ReactElement>()

  useEffect(() => {
    if (name && folder) {
      getComponent({ name, folder }).then((Component) =>
        setComponent(<Component />),
      )
    }
  }, [folder, name])

  return content
}

const getComponent = ({ name, folder }: { name: string; folder: string }) =>
  import(`../../../../examples/${folder}/${name}.example.tsx`).then((mod) => {
    const component = getComponentFromModule({ mod, name })

    if (!component) {
      throw new Error(
        `Example module "${folder}/${name}" does not export default, ${name}Example, ${name}, or exactly one uppercase named component export`,
      )
    }

    return component
  })

export function getComponentFromModule({
  mod,
  name,
}: {
  mod: ExampleModule
  name: string
}): ExampleComponent | undefined {
  for (const candidate of [mod.default, mod[`${name}Example`], mod[name]]) {
    if (isExampleComponent(candidate)) {
      return candidate
    }
  }

  // Last fallback covers single-component examples whose export name drifted
  // from the route name, while ambiguous modules fail loudly instead.
  const namedComponents = Object.entries(mod).flatMap(([exportName, value]) => {
    if (
      exportName !== 'default' &&
      /^[A-Z]/.test(exportName) &&
      isExampleComponent(value)
    ) {
      return [value]
    }

    return []
  })

  return namedComponents.length === 1 ? namedComponents[0] : undefined
}

function isExampleComponent(value: unknown): value is ExampleComponent {
  return typeof value === 'function'
}
